import { en, type TranslationKey } from './en';
import { sl } from './sl';

export type { TranslationKey };

export const LANGS = ['en', 'sl'] as const;
export type Lang = (typeof LANGS)[number];

/** Privzeti jezik je angleščina (CLAUDE.md). Slovenščina je enakovreden prevod. */
export const DEFAULT_LANG: Lang = 'en';

const DICTS: Record<Lang, Record<TranslationKey, string>> = { en, sl };

const STORAGE_KEY = 'nesywave.lang';

/**
 * Nedeljivi presledek (P4 §7). Zapisan s kodo in ne kot znak, ker ga
 * urejevalniki in orodja tiho zamenjajo z navadnim presledkom — nato pa se
 * `192` in `kbps` ob ožjem zaslonu prelomita na dve vrstici.
 */
const NBSP = String.fromCharCode(0xa0);

export type TranslationParams = Record<string, string | number>;

export function isLang(value: string | null): value is Lang {
  return value !== null && (LANGS as readonly string[]).includes(value);
}

export function readStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLang(stored) ? stored : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

export function storeLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Zasebni način brez shrambe — jezik velja le za to sejo.
  }
}

/**
 * `{ime}` v nizu zamenja vrednost iz `params`. `{nbsp}` je vgrajen in vedno
 * na voljo.
 *
 * Števila gredo skozi `Intl.NumberFormat` — nikoli konkatenacija (P4 §7).
 * V slovenščini tisočice loči pika, decimalke vejica; ročno sestavljanje
 * to razliko tiho izgubi.
 */
/**
 * Osnove ključev, ki imajo vse štiri množinske oblike.
 *
 * Tip obstaja zato, da `plural()` ne more dobiti ključa, ki nima vseh
 * oblik — manjkajoča bi bila napaka ob izvajanju, in to samo v slovenščini
 * in samo pri določenih številih. Natanko tista vrsta napake, ki jo je
 * najtežje opaziti.
 */
export type PluralKey =
  | 'backup.preview.total'
  | 'backup.preview.added'
  | 'backup.preview.duplicate'
  | 'settings.seed.restore.count'
  | 'settings.seed.restore.toast'
  | 'search.results.count'
  /**
   * Ojačitev pasu EQ (faza 6). Edini ključ, ki dobi tudi **negativna** števila:
   * `Intl.PluralRules` izbira po absolutni vrednosti, zato −3 dB pravilno pade
   * v `few` (»−3 decibele«). Pokrito s testom, ne domnevano.
   */
  | 'settings.eq.db';

/**
 * Množina prek `Intl.PluralRules` (P4 §9).
 *
 * ⚠️ **Slovenščina ima dvojino in štiri kategorije** — `one` (1 postaja),
 * `two` (2 postaji), `few` (3 postaje) in `other` (5 postaj). Vzorec
 * `count === 1 ? a : b` je zato napačen; pri dveh in pri treh da napačno
 * obliko in besedilo zveni kot strojni prevod.
 *
 * Angleščina uporabi le `one` in `other`; preostali dve obliki sta v
 * slovarju zato, ker je `sl` tipiziran kot `Record<TranslationKey, string>`
 * in mora imeti par za vsak ključ.
 */
export function plural(
  lang: Lang,
  base: PluralKey,
  count: number,
  params?: TranslationParams,
): string {
  const category = new Intl.PluralRules(lang).select(count);
  return translate(lang, `${base}.${category}` as TranslationKey, { n: count, ...params });
}

/**
 * Prevod za ključ, ki ga slovar morda nima — vrne `null` namesto napake.
 *
 * Obstaja zaradi **žanrov**: shranjeni so v angleščini, kot jih pošilja
 * radio-browser, in iz kataloga jih bo v fazi 4 prišlo na stotine. Vseh ni
 * mogoče prevesti vnaprej in ugibanje bi bilo slabše od surove oznake, zato
 * je izpis prevoda neobvezen. `translate()` ostane strog — manjkajoč ključ
 * mora tam ostati napaka ob prevajanju.
 */
export function translateOptional(lang: Lang, key: string): string | null {
  const value = (DICTS[lang] as Record<string, string | undefined>)[key];
  return value === undefined ? null : value.replaceAll('{nbsp}', NBSP);
}

export function translate(lang: Lang, key: TranslationKey, params?: TranslationParams): string {
  const template = DICTS[lang][key];
  if (params === undefined) return template.replaceAll('{nbsp}', NBSP);

  const numbers = new Intl.NumberFormat(lang);
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    if (name === 'nbsp') return NBSP;
    const value = params[name];
    if (value === undefined) return match;
    return typeof value === 'number' ? numbers.format(value) : value;
  });
}
